import React, { useState, useEffect, useCallback } from 'react';

const API = 'https://api.botanix.in/api';

const C = {
 bg:'#0A1628', surface:'#0F2040', surface2:'#0F2040',
 border:'rgba(30,58,95,.8)', border2:'rgba(30,58,95,1)',
 text:'#F0F2FF', text2:'#A0A8C0', text3:'#606880',
 accent:'#3AAF58', blue:'#FFFFFF', blueLight:'#FFFFFF',
 red:'#E05050', redBg:'rgba(224,80,80,.10)', redBorder:'rgba(224,80,80,.25)',
 amber:'#D4840F', amberBg:'rgba(212,132,15,.10)', amberBorder:'rgba(212,132,15,.25)',
 green:'#3AAF58', greenBg:'rgba(58,175,88,.10)', greenBorder:'rgba(58,175,88,.25)',
 purple:'#8060C0',
};

const STATUS_CONFIG = {
 'Vacant Clean': { color:'#3AAF58', bg:'rgba(58,175,88,.15)', },
 'Vacant Dirty': { color:'#D4840F', bg:'rgba(212,132,15,.15)', },
 'Occupied': { color:'#2563A8', bg:'rgba(37,99,168,.15)', },
 'Maintenance': { color:'#E05050', bg:'rgba(224,80,80,.15)', },
 'Blocked': { color:'#606880', bg:'rgba(96,104,128,.15)', },
};

const BOOKING_STATUS = {
 'Confirmed': { color:'#3AAF58', bg:'rgba(58,175,88,.25)', label:'Confirmed' },
 'Tentative': { color:'#D4840F', bg:'rgba(212,132,15,.25)', label:'Tentative' },
 'Checked In': { color:'#2563A8', bg:'rgba(37,99,168,.25)', label:'Checked In' },
 'Checked Out': { color:'#8060C0', bg:'rgba(128,96,192,.25)', label:'Checked Out' },
 'Cancelled': { color:'#606880', bg:'rgba(96,104,128,.2)', label:'Cancelled' },
 'No Show': { color:'#E05050', bg:'rgba(224,80,80,.2)', label:'No Show' },
};

const DAYS_SHORT = ['Su','Mo','Tu','We','Th','Fr','Sa'];
const inr = n => '₹' + Number(n||0).toLocaleString('en-IN',{maximumFractionDigits:0});
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short'}) : '—';
const fmt = d => new Date(d).toISOString().slice(0,10);

const inp = { width:'100%', padding:'9px 12px', background:'#22263A', border:'1px solid rgba(255,255,255,.14)', borderRadius:8, color:'#F0F2FF', fontSize:13, boxSizing:'border-box', outline:'none' };
const sel = { ...inp, cursor:'pointer' };

// ── ROOM GRID ─────────────────────────────────────────────────────────────────
function RoomGrid({ rooms, roomTypes, bookings, onStatusChange, loading }) {
 const [filterStatus, setFilterStatus] = useState('All');
 const [filterBlock, setFilterBlock] = useState('All');
 const [selected, setSelected] = useState(null);
 const [newStatus, setNewStatus] = useState('');

 const blocks = ['All',...new Set(rooms.map(r=>r.block_name||'Unassigned'))];

 let filtered = rooms;
 if (filterStatus!=='All') filtered = filtered.filter(r=>r.status===filterStatus);
 if (filterBlock !=='All') filtered = filtered.filter(r=>(r.block_name||'Unassigned')===filterBlock);

 const today = fmt(new Date());
 const getActiveBooking = roomNum => bookings.find(b =>
 b.start_date?.slice(0,10) <= today && b.end_date?.slice(0,10) >= today &&
 !['Cancelled','No Show'].includes(b.booking_status) &&
 b.notes?.toLowerCase().includes(`room ${roomNum}`.toLowerCase())
 );

 const stats = {
 total: rooms.length,
 occupied: rooms.filter(r=>r.status==='Occupied').length,
 vacant: rooms.filter(r=>r.status==='Vacant Clean').length,
 dirty: rooms.filter(r=>r.status==='Vacant Dirty').length,
 maint: rooms.filter(r=>r.status==='Maintenance').length,
 };

 return (
 <div>
 {/* Stats */}
 <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:12, marginBottom:20 }}>
 {[
 {label:'Total', value:stats.total, color:C.text, },
 {label:'Occupied', value:stats.occupied, color:C.blue, },
 {label:'Vacant Clean',value:stats.vacant, color:C.green, },
 {label:'Dirty', value:stats.dirty, color:C.amber, },
 {label:'Occupancy', value:`${stats.total?Math.round(stats.occupied/stats.total*100):0}%`, color:C.accent, },
 ].map(s=>(
 <div key={s.label} style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:'14px 16px' }}>
 <div style={{ fontSize:18, marginBottom:4 }}>{s.icon}</div>
 <div style={{ fontSize:22, fontWeight:900, color:s.color }}>{s.value}</div>
 <div style={{ fontSize:11, color:C.text3 }}>{s.label}</div>
 </div>
 ))}
 </div>

 {/* Filters */}
 <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
 <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)} style={{ ...sel, width:160 }}>
 <option style={{background:'#0F2040',color:'#F0F2FF'}} value="All">All Status</option>
 {Object.keys(STATUS_CONFIG).map(s=><option style={{background:'#0F2040',color:'#F0F2FF'}} key={s}>{s}</option>)}
 </select>
 <select value={filterBlock} onChange={e=>setFilterBlock(e.target.value)} style={{ ...sel, width:180 }}>
 {blocks.map(b=><option style={{background:'#0F2040',color:'#F0F2FF'}} key={b}>{b}</option>)}
 </select>
 </div>

 {loading && <div style={{ textAlign:'center', padding:40, color:C.text2 }}>Loading…</div>}

 {!loading && (
 <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(190px,1fr))', gap:12 }}>
 {filtered.map(room => {
 const st = STATUS_CONFIG[room.status]||STATUS_CONFIG['Vacant Clean'];
 const rt = roomTypes.find(t=>t.id===room.room_type_id);
 const isSel = selected?.id===room.id;
 return (
 <div key={room.id} onClick={()=>{ setSelected(isSel?null:room); setNewStatus(room.status); }}
 style={{ background:C.surface, border:`2px solid ${isSel?C.accent:st.color+'44'}`, borderRadius:14, padding:16, cursor:'pointer', transition:'border .2s' }}>
 <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
 <div style={{ fontSize:22, fontWeight:900, color:C.text }}>#{room.room_number}</div>
 <span style={{ padding:'3px 8px', borderRadius:6, fontSize:10, fontWeight:700, background:st.bg, color:st.color }}>{st.icon} {room.status}</span>
 </div>
 {room.block_name && <div style={{ fontSize:10, color:C.text3, marginBottom:4 }}>{room.block_name}</div>}
 <div style={{ fontSize:12, color:C.text2 }}>{rt?.name||'Standard'}</div>
 <div style={{ fontSize:11, color:C.text3 }}>Floor {room.floor||1} · {inr(rt?.base_tariff||0)}/night</div>
 {isSel && (
 <div style={{ marginTop:12, display:'flex', flexDirection:'column', gap:6 }} onClick={e=>e.stopPropagation()}>
 <select value={newStatus} onChange={e=>setNewStatus(e.target.value)} style={{ ...sel, padding:'6px 8px', fontSize:12 }}>
 {Object.keys(STATUS_CONFIG).map(s=><option style={{background:'#0F2040',color:'#F0F2FF'}} key={s}>{s}</option>)}
 </select>
 <button onClick={()=>{ onStatusChange(room.id,newStatus); setSelected(null); }}
 style={{ padding:'6px', background:C.accent, border:'none', borderRadius:6, color:'#fff', cursor:'pointer', fontSize:12, fontWeight:700 }}>
 Update Status
 </button>
 </div>
 )}
 </div>
 );
 })}
 </div>
 )}
 </div>
 );
}

// ── BOOKING CALENDAR ──────────────────────────────────────────────────────────
function BookingCalendar({ bookings, loading }) {
 const today = new Date();
 const [startDate, setStartDate] = useState(new Date(today.getFullYear(),today.getMonth(),1));
 const [numDays, setNumDays] = useState(30);
 const [tooltip, setTooltip] = useState(null);
 const [filterType, setFilterType] = useState('All');
 const [filterSales, setFilterSales] = useState('All');

 const days = Array.from({length:numDays},(_,i)=>{
 const d = new Date(startDate);
 d.setDate(startDate.getDate()+i);
 return d;
 });

 const todayStr = fmt(today);
 const salespeople = ['All',...new Set(bookings.map(b=>b.salesperson).filter(Boolean))];
 const bookingTypes = ['All',...new Set(bookings.map(b=>b.booking_type).filter(Boolean))];

 let filteredBookings = bookings;
 if (filterType!=='All') filteredBookings = filteredBookings.filter(b=>b.booking_type===filterType);
 if (filterSales!=='All') filteredBookings = filteredBookings.filter(b=>b.salesperson===filterSales);

 const getBookingsForDate = (date) => {
 const d = fmt(date);
 return filteredBookings.filter(b => b.start_date?.slice(0,10) <= d && b.end_date?.slice(0,10) >= d && b.booking_status!=='Cancelled');
 };

 // Group bookings by type for rows
 const bookingRows = filteredBookings.filter(b => {
 const start = b.start_date?.slice(0,10);
 const end = b.end_date?.slice(0,10);
 const periodStart = fmt(startDate);
 const periodEnd = fmt(days[days.length-1]);
 return start <= periodEnd && end >= periodStart;
 });

 return (
 <div>
 {/* Legend */}
 <div style={{ display:'flex', gap:14, marginBottom:14, flexWrap:'wrap', alignItems:'center' }}>
 {Object.entries(BOOKING_STATUS).map(([s,st])=>(
 <div key={s} style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color:C.text2 }}>
 <div style={{ width:12, height:12, borderRadius:3, background:st.color }} />
 {s}
 </div>
 ))}
 <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
 <select value={filterSales} onChange={e=>setFilterSales(e.target.value)} style={{ ...sel, width:160 }}>
 {salespeople.map(s=><option style={{background:'#0F2040',color:'#F0F2FF'}} key={s}>{s}</option>)}
 </select>
 <select value={filterType} onChange={e=>setFilterType(e.target.value)} style={{ ...sel, width:180 }}>
 {bookingTypes.map(t=><option style={{background:'#0F2040',color:'#F0F2FF'}} key={t}>{t}</option>)}
 </select>
 <select value={numDays} onChange={e=>setNumDays(parseInt(e.target.value))} style={{ ...sel, width:110 }}>
 <option style={{background:'#0F2040',color:'#F0F2FF'}} value={14}>14 Days</option>
 <option style={{background:'#0F2040',color:'#F0F2FF'}} value={30}>30 Days</option>
 <option style={{background:'#0F2040',color:'#F0F2FF'}} value={60}>60 Days</option>
 </select>
 </div>
 </div>

 {/* Nav */}
 <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:16 }}>
 <button onClick={()=>{const d=new Date(startDate);d.setDate(d.getDate()-numDays);setStartDate(d);}} style={{ padding:'6px 14px', background:C.surface, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text2, cursor:'pointer' }}></button>
 <div style={{ fontWeight:700, fontSize:13, color:C.text, minWidth:200, textAlign:'center' }}>
 {startDate.toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'})}
 </div>
 <button onClick={()=>{const d=new Date(startDate);d.setDate(d.getDate()+numDays);setStartDate(d);}} style={{ padding:'6px 14px', background:C.surface, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text2, cursor:'pointer' }}></button>
 <button onClick={()=>setStartDate(new Date(today.getFullYear(),today.getMonth(),today.getDate()))} style={{ padding:'6px 14px', background:C.surface, border:`1px solid ${C.border2}`, borderRadius:8, color:C.accent, cursor:'pointer', fontWeight:600 }}>Today</button>
 </div>

 {loading && <div style={{ textAlign:'center', padding:40, color:C.text2 }}>Loading…</div>}

 {!loading && (
 <div style={{ overflowX:'auto' }}>
 <table style={{ borderCollapse:'collapse', minWidth:600, width:'100%' }}>
 <thead>
 <tr>
 <th style={{ padding:'8px 12px', fontSize:12, color:C.text2, fontWeight:600, textAlign:'left', background:C.surface, position:'sticky', left:0, zIndex:2, minWidth:180, borderBottom:`1px solid ${C.border}` }}>
 Booking ({bookingRows.length})
 </th>
 {days.map((d,i) => {
 const isToday = fmt(d)===todayStr;
 const isWeekend = d.getDay()===0||d.getDay()===6;
 const dayBookings = getBookingsForDate(d);
 return (
 <th key={i} onMouseEnter={()=>dayBookings.length>0&&setTooltip({date:d,bookings:dayBookings})}
 onMouseLeave={()=>setTooltip(null)}
 style={{ padding:'4px 2px', fontSize:9, color:isToday?C.accent:isWeekend?C.amber:C.text3, fontWeight:isToday?900:600, textAlign:'center', minWidth:32, background:isToday?`${C.accent}20`:isWeekend?`${C.amber}08`:C.surface2, borderBottom:`1px solid ${C.border}`, borderLeft:`1px solid ${C.border}`, cursor:dayBookings.length>0?'pointer':'default' }}>
 <div>{DAYS_SHORT[d.getDay()]}</div>
 <div style={{ fontSize:10 }}>{d.getDate()}</div>
 {dayBookings.length>0 && <div style={{ fontSize:8, color:C.accent, fontWeight:800 }}>{dayBookings.length}</div>}
 </th>
 );
 })}
 </tr>
 </thead>
 <tbody>
 {bookingRows.length===0 && (
 <tr><td colSpan={numDays+1} style={{ textAlign:'center', padding:40, color:C.text3 }}>No bookings in this period</td></tr>
 )}
 {bookingRows.map(b => {
 const bst = BOOKING_STATUS[b.booking_status]||BOOKING_STATUS['Confirmed'];
 const startStr = b.start_date?.slice(0,10);
 const endStr = b.end_date?.slice(0,10);
 return (
 <tr key={b.id} style={{ borderBottom:`1px solid rgba(255,255,255,.04)` }}>
 <td style={{ padding:'8px 12px', background:C.surface, position:'sticky', left:0, zIndex:1, borderRight:`1px solid ${C.border}` }}>
 <div style={{ display:'flex', alignItems:'center', gap:6 }}>
 <div style={{ width:8, height:8, borderRadius:'50%', background:bst.color, flexShrink:0 }} />
 <div>
 <div style={{ fontWeight:700, fontSize:12, color:C.text }}>{b.guest_name||b.company_name||'—'}</div>
 <div style={{ fontSize:10, color:C.text3 }}>{b.booking_type} · {b.pax_count} pax</div>
 <div style={{ fontSize:10, color:C.text3 }}>{b.salesperson||'—'} · {b.booking_code}</div>
 </div>
 </div>
 </td>
 {days.map((d,i) => {
 const dateStr = fmt(d);
 const isInRange = dateStr>=startStr && dateStr<=endStr;
 const isStart = dateStr===startStr;
 const isEnd = dateStr===endStr;
 const isToday = dateStr===todayStr;
 return (
 <td key={i} style={{ padding:'3px 2px', minWidth:32, height:38, background:isToday?`${C.accent}08`:'transparent', borderLeft:`1px solid rgba(255,255,255,.03)` }}>
 {isInRange && (
 <div title={`${b.guest_name||'Guest'} · ${b.booking_status} · ${fmtDate(b.start_date)} - ${fmtDate(b.end_date)}`}
 style={{ height:32, background:bst.bg, borderLeft:isStart?`3px solid ${bst.color}`:'none', borderRadius:isStart?'6px 0 0 6px':isEnd?'0 6px 6px 0':'0', display:'flex', alignItems:'center', padding:'0 4px', overflow:'hidden' }}>
 {isStart && <div style={{ fontSize:9, fontWeight:700, color:bst.color, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
 {b.guest_name||b.company_name||'Guest'}
 </div>}
 </div>
 )}
 </td>
 );
 })}
 </tr>
 );
 })}
 </tbody>
 </table>
 </div>
 )}

 {/* Tooltip */}
 {tooltip && (
 <div style={{ marginTop:12, background:C.surface2, borderRadius:10, padding:12, border:`1px solid ${C.border2}` }}>
 <div style={{ fontWeight:700, fontSize:13, color:C.text, marginBottom:8 }}>
 {tooltip.date.toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long'})} — {tooltip.bookings.length} booking(s)
 </div>
 {tooltip.bookings.map((b,i) => {
 const bst = BOOKING_STATUS[b.booking_status]||BOOKING_STATUS['Confirmed'];
 return (
 <div key={i} style={{ display:'flex', gap:10, alignItems:'center', padding:'6px 0', borderBottom:`1px solid ${C.border}` }}>
 <div style={{ width:10, height:10, borderRadius:3, background:bst.color, flexShrink:0 }} />
 <div style={{ flex:1 }}>
 <div style={{ fontSize:12, fontWeight:600, color:C.text }}>{b.guest_name||b.company_name||'—'}</div>
 <div style={{ fontSize:11, color:C.text3 }}>{b.booking_type} · {b.pax_count} pax · {b.booking_code}</div>
 </div>
 <div style={{ textAlign:'right' }}>
 <div style={{ fontSize:11, fontWeight:700, color:bst.color, padding:'2px 8px', background:bst.bg, borderRadius:4 }}>{b.booking_status}</div>
 <div style={{ fontSize:10, color:C.text3, marginTop:2 }}>{b.salesperson||'—'}</div>
 </div>
 </div>
 );
 })}
 </div>
 )}
 </div>
 );
}

// ── MAIN ──────────────────────────────────────────────────────────────────────
export default function RoomManagement() {
 const [tab, setTab] = useState('calendar');
 const [rooms, setRooms] = useState([]);
 const [roomTypes, setRoomTypes] = useState([]);
 const [bookings, setBookings] = useState([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState('');

 const load = useCallback(async () => {
 setLoading(true); setError('');
 try {
 const [r, rt, b] = await Promise.all([
 fetch(`${API}/rooms`).then(x=>x.json()),
 fetch(`${API}/rooms/types`).then(x=>x.json()),
 fetch(`${API}/bookings`).then(x=>x.json()),
 ]);
 setRooms(Array.isArray(r)?r:[]);
 setRoomTypes(Array.isArray(rt)?rt:[]);
 setBookings(Array.isArray(b)?b:[]);
 } catch(e) { setError(e.message); }
 finally { setLoading(false); }
 }, []);

 useEffect(() => { load(); }, [load]);

 const handleStatusChange = async (roomId, status) => {
 try {
 await fetch(`${API}/rooms/${roomId}/status`, {
 method:'PUT', headers:{'Content-Type':'application/json'},
 body: JSON.stringify({ status }),
 });
 setRooms(p=>p.map(r=>r.id===roomId?{...r,status}:r));
 } catch(e) {
 // Fallback — update via main rooms endpoint
 await fetch(`${API}/rooms/${roomId}`, {
 method:'PUT', headers:{'Content-Type':'application/json'},
 body: JSON.stringify({ status }),
 });
 setRooms(p=>p.map(r=>r.id===roomId?{...r,status}:r));
 }
 };

 const TABS = [
 { id:'calendar', label:' Booking Calendar' },
 { id:'grid', label:' Room Grid' },
 ];

 return (
 <div style={{ padding:24, background:C.bg, minHeight:'100vh', color:C.text, fontFamily:'Inter,sans-serif' }}>
 <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
 <div>
 <div style={{ fontSize:22, fontWeight:800 }}> Room Management</div>
 <div style={{ fontSize:13, color:C.text2, marginTop:2 }}>{bookings.length} bookings · {rooms.length} rooms</div>
 </div>
 <button onClick={load} style={{ padding:'9px 18px', background:C.surface, border:`1px solid ${C.border2}`, borderRadius:8, color:C.text2, cursor:'pointer', fontSize:13 }}> Refresh</button>
 </div>

 {error && <div style={{ padding:'10px 14px', background:C.redBg, border:`1px solid ${C.redBorder}`, borderRadius:8, color:C.red, fontSize:13, marginBottom:16 }}> {error}</div>}

 <div style={{ display:'flex', gap:4, marginBottom:20, background:C.surface, borderRadius:10, padding:4, width:'fit-content' }}>
 {TABS.map(t=>(
 <button key={t.id} onClick={()=>setTab(t.id)}
 style={{ padding:'8px 20px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, fontWeight:600, background:tab===t.id?C.accent:'transparent', color:tab===t.id?'#fff':C.text2 }}>
 {t.label}
 </button>
 ))}
 </div>

 {tab==='calendar' && <BookingCalendar bookings={bookings} loading={loading} />}
 {tab==='grid' && <RoomGrid rooms={rooms} roomTypes={roomTypes} bookings={bookings} onStatusChange={handleStatusChange} loading={loading} />}
 </div>
 );
}
